/**
 * What produced a grade, pinned per attempt.
 *
 * The host stores this string with the attempt record alongside the scheme, so a
 * regrade months later can reproduce what the student actually saw. That only
 * works if the string changes whenever behaviour does.
 *
 * **Bump SCORING_VERSION for any observable change in grading** — a new item
 * type, a changed normalization rule, a different aggregation result, a fixed
 * bug. "It was only a bug fix" is exactly the case that silently changes last
 * term's grades, which is what versioning here exists to prevent.
 *
 * 1.1.0 added `ordering` and 1.2.0 added `matching`, both with partial credit. Additive — no
 * existing grade changes — but a regrade of one of those items needs that version or later,
 * and the record must be able to say so.
 *
 * **2.0.0 is the first bump that is not additive.** The `he` profile now strips geresh,
 * gershayim and the quote characters mistaken for them, so answers that were marked wrong
 * before — a learner's `צה״ל` against an author's `צה"ל`, identical on screen — are now right.
 * That is the intended fix and it means replaying a stored response under 2.0.0 can produce a
 * different verdict than the attempt recorded. A major bump, because "the same responses score
 * the same" is exactly the promise being broken, and a regrade must be able to ask which side
 * of the change an attempt sits on.
 *
 * 2.1.0 adds `parts` to the verdicts of `ordering` and `matching` — which piece was right.
 * Additive: no points change, and a consumer that ignores the field grades identically.
 *
 * 2.2.0 adds `multiChoice` — tick every one that applies. Additive: no existing item's grade
 * changes. A regrade of one of these items needs this version or later, and the record has to be
 * able to say so.
 *
 * It is the first type whose **marking rule is a scheme field with more than two values**, because
 * "select all that apply" is where the established systems disagree: `all` (exact set, the
 * default), `partial` (a wrong tick cancels a right one), `perOption` (every option is its own
 * decision, for multiple true-false). All three are pinned by fixtures. Note what that means for
 * this version string — changing an item's `credit` changes its grade without changing any
 * version, exactly as it already does for `ordering` and `matching`. `credit` is part of the
 * scheme the host pins, which is what makes that reproducible.
 *
 * 2.3.0 adds `numeric` — type a number, into one field or several, marked against an inclusive
 * interval. Additive: no existing item's grade changes, and a regrade of one of these items needs
 * this version or later.
 *
 * Two things about it are worth knowing at this level rather than only in the item file. It is the
 * first type that **parses** the learner's answer rather than comparing it, so `"2,5"` and
 * `"2.5abc"` are wrong rather than errors, and `""` is blank rather than zero. And it is the first
 * whose answer is **not unique**, which is why its scheme carries `display` for solution mode —
 * an interval has infinitely many correct answers and none of them can be derived.
 *
 * The comparison does no arithmetic on the learner's number, deliberately: `min <= x <= max`
 * against authored literals is exact, where the `|x - answer| <= tolerance` form this type could
 * have had marks a genuinely correct 4.4 wrong against 4.35 ± 0.05. That is a grading decision
 * pinned by fixtures, not an implementation detail.
 *
 * 2.4.0 adds `judge` to `textEntry` (D93): an answer no accepted string matches is marked by the
 * AI's judgement of it, an input handed in as a seed is, or is `correct: null` with none. Additive:
 * an item without `judge` grades exactly as before.
 *
 * 2.5.0 carries a judgement's rubric scores by criterion and the AI's flags onto the verdict, as
 * `scores` and `flags` (D110). Additive: the points are the judgement's as before, and a consumer
 * that ignores the two fields grades identically.
 */
export const SCORING_VERSION = '2.5.0';

/**
 * The Unicode data version the text profiles were fixture-verified against.
 *
 * `String.prototype.normalize` is the one impurity the scorer accepts: its
 * result depends on the engine's Unicode data, which is the same class of hazard
 * as the `Intl` functions the design bans outright. Everything else in
 * `normalize.ts` is explicit code-point ranges precisely so that this is the
 * *only* place engine data enters a grade.
 *
 * A test asserts this matches `process.versions.unicode`, so an engine upgrade
 * shows up as a failing test with a decision to make, rather than as a quiet
 * change in results. Hebrew has no composed niqqud to reorder, so today's
 * exposure is small; Arabic in Phase 2 is where it would actually bite.
 */
export const UNICODE_VERIFIED_AGAINST = '15.1';
